import React, { useEffect, useState, useRef } from "react";
import {
  Box, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, Skeleton, useTheme, useMediaQuery,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useNavigate } from "react-router-dom";
import { getPortfolioOverallRatings } from "../../../api/portfolio_api";
import { getPortfolioRatingNarrative } from "../../../api/api";
import { ACTIVE_MODEL } from "../../../config/modelConfig";
import { getCurrencySymbol } from "../../../utils/currency";

/* ─────────────────────────────────────────
   Grade → theme color
───────────────────────────────────────── */
const useGradeColor = () => {
  const theme = useTheme();
  const g = theme.palette.grades;

  return (grade) => {
    if (!grade) return g.unknown;
    switch (grade.toUpperCase()) {
      case "A+": return g.aPlus;
      case "A": return g.a;
      case "A-": return g.aMinus;
      case "B+": return g.bPlus;
      case "B": return g.b;
      case "B-": return g.bMinus;
      case "C+": return g.cPlus;
      case "C": return g.c;
      case "C-": return g.cMinus;
      case "D+": return g.dPlus;
      case "D": return g.d;
      case "D-": return g.dMinus;
      case "E+": return g.ePlus ?? "#FF6D00";
      case "E": return g.e ?? "#E53935";
      case "E-": return g.eMinus ?? "#C62828";
      case "F": return g.f;
      default: return g.unknown;
    }
  };
};

/* ─────────────────────────────────────────
   Score Bar
───────────────────────────────────────── */
const ScoreBar = ({ score, color }) => {
  const val = Number(score) || 0;
  return (
    <div style={{
      width: "100%",
      height: 3,
      backgroundColor: "var(--color-border-primary)",
      borderRadius: "var(--radius-full)",
      overflow: "hidden",
      marginTop: 2,
    }}>
      <div style={{
        width: `${Math.min(val, 100)}%`,
        height: "100%",
        backgroundColor: color,
        borderRadius: "var(--radius-full)",
        transition: "width 0.6s ease",
      }} />
    </div>
  );
};

/* ─────────────────────────────────────────
   Mobile Rating Card — one card per ticker
───────────────────────────────────────── */
const MobileRatingCard = ({ r, factorColumns, gradeColor }) => {
  const navigate = useNavigate();
  const overallColor = gradeColor(r.overall_grade);

  return (
    <div style={{
      backgroundColor: "var(--color-surface)",
      border: "1px solid var(--color-border-primary)",
      borderRadius: "var(--radius-lg)",
      padding: "var(--spacing-md)",
      marginBottom: "var(--spacing-sm)",
    }}>
      {/* Ticker + Overall */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--spacing-sm)" }}>
        <Typography
          variant="paragraph"
          sx={{
            fontWeight: "var(--font-weight-bold)",
            color: "var(--color-primary)",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => navigate(`/stocks/${r.ticker}/details`)}
        >
          {r.ticker}
        </Typography>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-sm)" }}>
          <Typography variant="chipText" sx={{ color: overallColor }}>
            {r.overall_grade || "—"}
          </Typography>
          <Typography variant="tableHeader" sx={{ color: overallColor }}>
            {r.overall_rating || "—"}
          </Typography>
        </div>
      </div>

      {/* Factor rows */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--spacing-sm)" }}>
        {factorColumns.map((col) => {
          const color = gradeColor(r[col.gradeKey]);
          return (
            <div key={col.key} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="tableHeader" sx={{ color: "text.muted" }}>
                  {col.label}
                </Typography>
                <Typography variant="chipText" sx={{ color }}>
                  {r[col.gradeKey] || "—"}
                </Typography>
              </div>
              <ScoreBar score={r[col.scoreKey]} color={color} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   Typing Narrative
───────────────────────────────────────── */
const TypingNarrative = ({ text, speed = 18 }) => {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  const idxRef = useRef(0);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);

  useEffect(() => {
    if (!text) return;
    setDisplayed("");
    setDone(false);
    idxRef.current = 0;
    lastTimeRef.current = null;

    const step = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const elapsed = timestamp - lastTimeRef.current;
      if (elapsed >= speed) {
        const charsToAdd = Math.max(1, Math.floor(elapsed / speed));
        idxRef.current = Math.min(idxRef.current + charsToAdd, text.length);
        setDisplayed(text.slice(0, idxRef.current));
        lastTimeRef.current = timestamp;
      }
      if (idxRef.current < text.length) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDone(true);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [text, speed]);

  if (!text) return null;

  if (done) {
    return (
      <Typography variant="paragraph" sx={{ color: "text.secondary", lineHeight: 1.8, display: "inline" }}>
        {text}
      </Typography>
    );
  }

  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontFamily: "inherit",
        fontSize: "var(--font-size-base)",
        lineHeight: 1.8,
        color: "text.secondary",
        display: "inline",
      }}
    >
      {displayed}
      <Box
        component="span"
        sx={{
          display: "inline-block",
          width: "2px",
          height: "1em",
          bgcolor: "primary.main",
          ml: "2px",
          verticalAlign: "text-bottom",
          animation: "cursorBlink 0.7s step-start infinite",
          "@keyframes cursorBlink": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0 },
          },
        }}
      />
    </Box>
  );
};

/* ══════════════════════════════════════════
   OVERALL RATING TAB
══════════════════════════════════════════ */
const OverallRatingTab = ({ portfolio }) => {
  const navigate = useNavigate();
  const gradeColor = useGradeColor();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [ratings, setRatings] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(true);
  const [narrative, setNarrative] = useState("");
  const [narrativeLoading, setNarrativeLoading] = useState(true);

  const safeRatings = Array.isArray(ratings) ? ratings : [];

  const sym = getCurrencySymbol();

  useEffect(() => {
    const fetchAll = async () => {
      setRatingsLoading(true);
      setNarrativeLoading(true);

      const res = await getPortfolioOverallRatings(portfolio.id);
      if (res?.authExpired) { navigate("/login"); return; }

      let ratingsData = [];
      let healthScore = null;
      let healthGrade = "";

      if (res?.success && res.data?.response_data) {
        const rd = res.data.response_data;
        ratingsData = Array.isArray(rd.ratings) ? rd.ratings : [];
        healthScore = rd.health_score ?? null;
        healthGrade = rd.health_grade || "";
        setRatings(ratingsData);
      }
      setRatingsLoading(false);

      if (ratingsData.length > 0) {
        try {
          const payload = {
            health_score: healthScore,
            health_grade: healthGrade,
            stocks: ratingsData.map((r) => ({
              ticker: r.ticker,
              overall_rating: r.overall_rating || "",
              overall_grade: r.overall_grade || "",
              overall_score: Number(r.overall_score || 0),
              valuation_grade: r.valuation_grade || "",
              growth_grade: r.growth_grade || "",
              momentum_grade: r.momentum_grade || "",
              financial_profit_grade: r.financial_profit_grade || "",
              risk_grade: r.risk_grade || "",
            })),
          };
          const narRes = await getPortfolioRatingNarrative(payload);
          if (narRes?.authExpired) { navigate("/login"); return; }
          if (narRes?.success && narRes.data?.ai_narrative) {
            setNarrative(narRes.data.ai_narrative);
          }
        } catch (_) { /* narrative is optional */ }
      }
      setNarrativeLoading(false);
    };

    fetchAll();
  }, [portfolio.id]);

  const factorColumns = [
    { key: "valuation", label: "Valuation", gradeKey: "valuation_grade", scoreKey: "valuation_score" },
    { key: "growth", label: "Growth", gradeKey: "growth_grade", scoreKey: "growth_score" },
    { key: "momentum", label: "Momentum", gradeKey: "momentum_grade", scoreKey: "momentum_score" },
    {
      key: "financial",
      label: ACTIVE_MODEL === "return" ? "Profitability" : "Quality",
      gradeKey: "financial_profit_grade",
      scoreKey: "financial_profit_score",
    },
    { key: "risk", label: "Risk", gradeKey: "risk_grade", scoreKey: "risk_score" },
  ];

  /* ── Loading skeleton ── */
  if (ratingsLoading) return (
    <Box>
      <Skeleton variant="rectangular" width="100%" height={60}
        sx={{ bgcolor: "var(--color-surface)", borderRadius: "var(--radius-lg)", mb: 2 }} />
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rectangular" width="100%" height={isMobile ? 120 : 52}
          sx={{ bgcolor: "var(--color-surface)", borderRadius: "var(--radius-sm)", mb: 1 }} />
      ))}
    </Box>
  );

  /* ── Empty state ── */
  if (!ratingsLoading && safeRatings.length === 0) return (
    <div style={{
      backgroundColor: "var(--color-surface)",
      border: "1px solid var(--color-border-primary)",
      borderRadius: "var(--radius-xl)",
      padding: "var(--spacing-xl)",
      textAlign: "center",
    }}>
      <Typography variant="paragraph" sx={{ color: "text.secondary" }}>
        No FA ratings available for holdings.
      </Typography>
    </div>
  );

  return (
    <Box>
      {/* ── Factor Ratings ── */}
      <div style={{
        backgroundColor: isMobile ? "transparent" : "var(--color-surface)",
        border: isMobile ? "none" : "1px solid var(--color-border-primary)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
        marginBottom: "var(--spacing-lg)",
      }}>
        {/* Header */}
        <div style={{
          padding: "var(--spacing-sm) var(--spacing-md)",
          borderBottom: "1px solid var(--color-border-primary)",
          backgroundColor: "var(--color-surface)",
          borderRadius: isMobile ? "var(--radius-lg)" : 0,
          marginBottom: isMobile ? "var(--spacing-sm)" : 0,
        }}>
          <Typography variant="paragraph" sx={{ color: "text.primary", fontWeight: "var(--font-weight-bold)" }}>
            FA Ratings
          </Typography>
        </div>

        {/* ── MOBILE: cards ── */}
        {isMobile ? (
          <Box>
            {safeRatings.map((r) => (
              <MobileRatingCard
                key={r.ticker}
                r={r}
                factorColumns={factorColumns}
                gradeColor={gradeColor}
              />
            ))}
          </Box>
        ) : (
          /* ── DESKTOP: table ── */
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "var(--color-bg-tertiary)" }}>
                {["Ticker", "FA Grade", "FA Rating", ...factorColumns.map((f) => f.label)].map((h, idx) => (
                  <TableCell
                    key={idx}
                    align={idx >= 3 ? "center" : "left"}
                    sx={{
                      fontSize: "var(--font-size-sm)",
                      fontWeight: "var(--font-weight-bold)",
                      color: "var(--color-text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      borderBottom: "1px solid var(--color-border-primary)",
                      py: 1.25,
                      px: 1.5,
                      whiteSpace: "nowrap",
                      ...(idx === 0 && { width: 90 }),
                      ...(idx === 1 && { width: 90 }),
                      ...(idx === 2 && { width: 110 }),
                      ...(idx >= 3 && { width: `${100 / factorColumns.length}%` }),
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {safeRatings.map((r) => {
                const overallColor = gradeColor(r.overall_grade);
                return (
                  <TableRow
                    key={r.ticker}
                    sx={{ "&:hover": { bgcolor: "var(--color-surface-hover)" } }}
                  >
                    <TableCell sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}>
                      <Typography
                        variant="paragraph"
                        sx={{
                          fontWeight: "var(--font-weight-bold)",
                          color: "var(--color-primary)",
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => navigate(`/stocks/${r.ticker}/details`)}
                      >
                        {r.ticker}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}>
                      <Typography variant="chipText" sx={{ color: overallColor }}>
                        {r.overall_grade || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}>
                      <Typography variant="tableHeader" sx={{ color: overallColor }}>
                        {r.overall_rating || "—"}
                      </Typography>
                    </TableCell>

                    {factorColumns.map((col) => (
                      <TableCell key={col.key} align="center" sx={{ py: 1.5, px: 1, borderBottom: "none" }}>
                        <Typography variant="chipText" sx={{ color: gradeColor(r[col.gradeKey]) }}>
                          {r[col.gradeKey] || "—"}
                        </Typography>
                        <ScoreBar score={r[col.scoreKey]} color={gradeColor(r[col.gradeKey])} />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* ── AI Narrative ── */}
      <div style={{
        backgroundColor: "var(--color-surface)",
        border: "1px solid var(--color-border-primary)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
      }}>
        <div style={{
          padding: "var(--spacing-sm) var(--spacing-md)",
          borderBottom: "1px solid var(--color-border-primary)",
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-sm)",
        }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: "var(--color-primary)" }} />
          <Typography variant="paragraph" sx={{ color: "text.primary", fontWeight: "var(--font-weight-bold)" }}>
            FA Analysis
          </Typography>
        </div>

        <div style={{ padding: "var(--spacing-lg)" }}>
          {narrativeLoading ? (
            <Box>
              {[100, 90, 75, 85].map((w, i) => (
                <Skeleton key={i} variant="rectangular" width={`${w}%`} height={14}
                  sx={{ bgcolor: "var(--color-surface-hover)", borderRadius: "var(--radius-sm)", mb: i < 3 ? 0.75 : 0 }} />
              ))}
            </Box>
          ) : narrative ? (
            <TypingNarrative text={narrative} speed={6} />
          ) : (
            <Typography variant="paragraph" sx={{ color: "text.muted", fontStyle: "italic" }}>
              Portfolio analysis narrative unavailable.
            </Typography>
          )}
        </div>
      </div>
    </Box>
  );
};

export default OverallRatingTab;